import { useEffect, useState } from "react";

// Editor "Data fields" tab (template autofill): the designer picks
// the text and image elements an app fills in and names them. An app then
// renders this template by its ID, sending values keyed by those names
// (POST /v1/render; GET /v1/templates/{id}/fields lists them).

const FIELD_TYPES = new Set(["text", "image"]);

// Field names are API keys: letters, digits and underscores, starting with a
// letter. Spaces and dashes become underscores as you type.
export const cleanFieldName = (value) =>
  value
    .trim()
    .replace(/[\s-]+/g, "_")
    .replace(/[^A-Za-z0-9_]/g, "")
    .replace(/^[^A-Za-z]+/, "");

const suggestName = (obj) => {
  const base = cleanFieldName(obj.layerName || "").toLowerCase();
  return base && !/^(text|image|frame)_?\d*$/.test(base) ? base : obj.layerType === "image" ? "image" : "text";
};

function TypeIcon({ type }) {
  return type === "image" ? (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 5h16v14H4zM6.5 16l3.5-4.5 2.5 3 2-2.5 3 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 5h14v3h-5.5v11h-3V8H5z" fill="currentColor" />
    </svg>
  );
}

export default function DataFieldsPanel({ objects, selected, templateId, onSelect, onUpdate }) {
  const fields = objects.filter((o) => o.dataField && FIELD_TYPES.has(o.layerType));
  const nameCounts = fields.reduce((m, o) => ({ ...m, [o.layerName]: (m[o.layerName] || 0) + 1 }), {});

  const canAdd = selected && FIELD_TYPES.has(selected.layerType) && !selected.dataField;
  const [newName, setNewName] = useState("");
  useEffect(() => {
    if (canAdd) setNewName(suggestName(selected));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, canAdd]);

  const [editing, setEditing] = useState(null); // layerId being renamed
  const [draft, setDraft] = useState("");
  const [copied, setCopied] = useState(false);

  const taken = (name, except) => fields.some((o) => o !== except && o.layerName === name);
  const addError = !newName ? "Give it a name." : taken(newName) ? "Another field already has this name." : null;

  const add = () => {
    if (addError) return;
    onUpdate(selected, { layerName: newName, dataField: true });
  };
  const commitRename = (obj) => {
    const name = cleanFieldName(draft);
    if (name && !taken(name, obj)) onUpdate(obj, { layerName: name });
    setEditing(null);
  };
  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(templateId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1300);
    } catch {
      /* clipboard blocked */
    }
  };

  return (
    <div className="df-panel">
      <h4>Data fields</h4>
      <p className="flyout-hint">
        Select a text or image element and name it. Job content fills these in by name when a design is generated.
      </p>

      {templateId && (
        <div className="df-id">
          <span>Template ID</span>
          <code title={templateId}>{templateId}</code>
          <button type="button" onClick={copyId}>{copied ? "Copied" : "Copy"}</button>
        </div>
      )}

      {canAdd && (
        <div className="df-add">
          <label htmlFor="df-new-name">
            Add this {selected.layerType === "image" ? "image" : "text"} as a data field
          </label>
          <div className="df-add-row">
            <input
              id="df-new-name"
              value={newName}
              placeholder="e.g. headline"
              onChange={(e) => setNewName(cleanFieldName(e.target.value))}
              onKeyDown={(e) => e.key === "Enter" && add()}
            />
            <button type="button" onClick={add} disabled={Boolean(addError)}>Add</button>
          </div>
          {addError && newName && <p className="df-error">{addError}</p>}
        </div>
      )}
      {selected && !FIELD_TYPES.has(selected.layerType) && (
        <p className="flyout-hint">Only text and image elements can be data fields.</p>
      )}
      {!selected && <p className="flyout-hint">Select an element on the canvas to add it.</p>}

      <div className="df-count">
        {fields.length} data field{fields.length === 1 ? "" : "s"}
      </div>
      {fields.length === 0 && <p className="flyout-hint">No data fields yet.</p>}
      <ul className="df-list">
        {fields.map((obj) => (
          <li
            key={obj.layerId}
            className={`df-item ${selected === obj ? "active" : ""} ${nameCounts[obj.layerName] > 1 ? "dup" : ""}`}
          >
            <span className="df-type" title={obj.layerType}><TypeIcon type={obj.layerType} /></span>
            {editing === obj.layerId ? (
              <input
                className="df-rename"
                value={draft}
                autoFocus
                onChange={(e) => setDraft(cleanFieldName(e.target.value))}
                onBlur={() => commitRename(obj)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename(obj);
                  if (e.key === "Escape") setEditing(null);
                }}
              />
            ) : (
              <button type="button" className="df-name" onClick={() => onSelect(obj)} title="Select on canvas">
                {obj.layerName}
              </button>
            )}
            <button
              type="button"
              className="df-icon"
              title="Rename"
              onClick={() => {
                setEditing(obj.layerId);
                setDraft(obj.layerName);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 20h4L19 9l-4-4L4 16zM14 6l4 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              type="button"
              className="df-icon"
              title="Remove as data field (keeps the element)"
              onClick={() => onUpdate(obj, { dataField: false })}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 7h14M10 7V4h4v3M7 7l1 13h8l1-13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
              </svg>
            </button>
          </li>
        ))}
      </ul>
      {Object.values(nameCounts).some((n) => n > 1) && (
        <p className="df-error">Two fields share a name, so both would get the same value.</p>
      )}

      {fields.length > 0 && templateId && (
        <div className="df-snippet">
          <span>Render it from code</span>
          <pre>{`POST /v1/render
{
  "template": "${templateId}",
  "layers": {
${fields
  .map((o) => `    "${o.layerName}": { ${o.layerType === "image" ? '"image_url": "https://…"' : '"text": "…"'} }`)
  .join(",\n")}
  }
}`}</pre>
        </div>
      )}
    </div>
  );
}
